/**
 * <JoinRoomForm>: name input + color picker for joining a game room.
 *
 * Per IMPLEMENTATION_PLAN §7 M3.T6.
 *
 * Shown when a player navigates to /r/:id before they have a name set.
 * Validates:
 *   - Name is 3-20 characters (after NFD trim)
 *   - Color is one of the 8 brand colors
 *
 * On submit, calls onJoin(name, color) so the parent can send the HELLO
 * frame with name+color and establish the WS room connection.
 *
 * The component handles:
 *   - Auto-focus on the name input
 *   - Submit on Enter key
 *   - Visual "Player Color" swatch picker, defaulting to a random UNUSED color
 *     (T17: `takenColors` comes from GET /api/room/:id; the `rng` seam keeps
 *     the default deterministic in tests)
 *   - An optional clickable 'drawlol' wordmark → onBack (T17 2b)
 */

import { useState, useEffect, useRef } from "preact/hooks";
import {
  BRAND_COLORS as SHARED_BRAND_COLORS,
  normalize,
  type BrandColor as SharedBrandColor,
} from "@drawlol/shared";
import { ScribbleButton } from "./ScribbleButton.js";
import { colorName } from "./ColorReassignNotice.js";

export type JoinRoomFormProps = {
  roomId: string;
  onJoin: (name: string, color: string) => void;
  /**
   * T17: colors already taken by current players in this room (from
   * GET /api/room/:id). The default color is picked at random from the
   * UN-taken colors so two players rarely collide on join.
   */
  takenColors?: ReadonlyArray<string>;
  /**
   * T17: injectable RNG in [0,1) for a DETERMINISTIC default-color pick in
   * tests. Defaults to Math.random in production.
   */
  rng?: () => number;
  /** T17 (2b): clickable 'drawlol' wordmark → back to home. Hidden when absent. */
  onBack?: () => void;
};

/**
 * Pick the initial color: a random color NOT in `takenColors`. When every
 * color is taken (a full 8-player room), fall back to a random brand color.
 * `rng` is injected so tests are deterministic.
 */
function pickDefaultColor(takenColors: ReadonlyArray<string>, rng: () => number): string {
  const taken = new Set(takenColors);
  const available = BRAND_COLORS.filter((c) => !taken.has(c));
  const pool = available.length > 0 ? available : BRAND_COLORS;
  const idx = Math.min(pool.length - 1, Math.floor(rng() * pool.length));
  return pool[idx] ?? BRAND_COLORS[0] ?? "#000000";
}

/**
 * The 8-color brand palette available for player selection.
 * M3.fix B7: this re-export points at the single definition in
 * @drawlol/shared so server, client, and tests can't drift apart.
 */
export const BRAND_COLORS = SHARED_BRAND_COLORS;

export type BrandColor = SharedBrandColor;

function validateName(name: string): string | null {
  // M3.fix B15: use the canonical normalize() pipeline so the client-side
  // gate matches the server-side gate exactly (one definition).
  const normalized = normalize(name);
  if (normalized.length < 3) return "Names need at least 3 letters.";
  if (normalized.length > 20) return "20 letters max.";
  return null;
}

export function JoinRoomForm({
  roomId,
  onJoin,
  takenColors = [],
  rng = Math.random,
  onBack,
}: JoinRoomFormProps) {
  const [name, setName] = useState("");
  // T17: default to a random UNUSED color, computed once on mount. The lazy
  // initializer runs the RNG a single time so the default is stable across
  // re-renders (and deterministic under an injected rng in tests).
  const [color, setColor] = useState<string>(() => pickDefaultColor(takenColors, rng));
  // T17: whether the user has manually picked a swatch. `takenColors` arrives
  // from an ASYNC fetch (RoomShell) AFTER first render, so the lazy initializer
  // above runs against an empty list and can't subtract taken colors. Recompute
  // the default once the real list arrives, but only while the pick is still
  // the auto-default, so we never clobber a deliberate user choice.
  const [colorTouched, setColorTouched] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the name input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // T17: re-pick the default from the freshly-fetched `takenColors` so the
  // default actually avoids colors already in use. Skips once the user has
  // touched the picker so a manual choice is never overridden.
  // `rng` is a stable seam (Math.random or a test fn); gate only on the
  // inputs that change the chosen default, takenColors and colorTouched.
  useEffect(() => {
    if (!colorTouched) setColor(pickDefaultColor(takenColors, rng));
  }, [takenColors, colorTouched]);

  // Live error after the user has typed at least one char. Without this
  // the button just sits disabled and there's nothing on screen to
  // explain why: users typed two chars, saw nothing, and didn't
  // realize there's a minimum.
  const liveError = touched && name.length > 0 ? validateName(name) : null;
  const displayError = nameError ?? liveError;

  const handleSubmit = () => {
    const error = validateName(name);
    if (error) {
      setNameError(error);
      return;
    }
    setNameError(null);
    onJoin(name.trim(), color);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div class="join-room-form" data-testid="join-room-form" data-room-id={roomId}>
      {onBack && (
        <button
          type="button"
          class="join-room-wordmark"
          onClick={onBack}
          aria-label="drawlol — back to home"
        >
          drawlol
        </button>
      )}
      <h2 class="join-room-title">Join the room</h2>
      <p class="join-room-subtitle">Room code: {roomId}</p>

      <div class="join-room-fields">
        <div class="notebook-input-wrapper">
          <label class="notebook-label" for="join-name-input">
            Your name
          </label>
          <input
            ref={inputRef}
            id="join-name-input"
            class="notebook-input"
            type="text"
            value={name}
            maxLength={20}
            onInput={(e) => {
              setName((e.target as HTMLInputElement).value);
              setTouched(true);
              // Clear the post-submit error message as soon as the user
              // edits: the live error below will re-display if still
              // invalid, but a stale submit-error shouldn't linger.
              if (nameError) setNameError(null);
            }}
            onKeyDown={handleKeyDown}
            aria-invalid={displayError ? "true" : "false"}
            aria-describedby={displayError ? "join-name-error" : undefined}
          />
        </div>
        {/* Always in the DOM so the reserved-height slot (CSS min-height)
            keeps the column from reflowing as the error toggles. `role=alert`
            still announces the text when it appears; empty otherwise. */}
        <p class="join-room-name-error" id="join-name-error" role="alert">
          {displayError ?? ""}
        </p>

        <div class="join-room-color-picker" role="group" aria-label="Pick a color">
          <p class="join-room-color-label">Your color</p>
          <div class="join-room-color-swatches">
            {BRAND_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                class={["color-swatch", color === c && "color-swatch--active"]
                  .filter(Boolean)
                  .join(" ")}
                style={{ backgroundColor: c }}
                onClick={() => {
                  setColor(c);
                  setColorTouched(true);
                }}
                aria-label={`Pick ${colorName(c)}`}
                aria-pressed={color === c}
              />
            ))}
          </div>
        </div>
      </div>

      <ScribbleButton
        variant="primary"
        onClick={handleSubmit}
        disabled={normalize(name).length < 3}
        aria-label="Join the room"
      >
        Join the room
      </ScribbleButton>
    </div>
  );
}
